import React from 'react';
import { Controller } from 'react-hook-form';
import { ChevronDown, Info } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { FormField, Input, Select, Textarea } from '../../../components/ui/FormComponents';
import { INSTALLATION_TRADES } from '../utils/teamMemberLabels';
import { useEmployeeForm } from '../hooks/useEmployeeForm';
import { Toast } from '../../../components/ui/Toast';

const CustomRoleSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  /*developer: Nithin
      date : 2024-06-19
      changes : Replaced Telecaller With Staff  and commented not required roles */

  const options = [
    { value: "admin", label: "Admin" },
    { value: "designer", label: "Designer" },
    // { value: 'designer', label: "Project Manager" },
    // { value: 'installation_team', label: "Installation Member" },
    { value: "accountant", label: "Accountant" },
    { value: 'store_incharge', label: "Store Incharge" },
    { value: "field_marketing", label: "Field Marketing" },
    { value: "client_coordinator", label: "Client Coordinator" },
    { value: "social_media", label: "Social Media" },
  ];

  const selectedLabel = options.find(o => o.value === value)?.label ||
    (value === "staff" ? "Client Coordinator" :
     value === "sales_executive" ? "Field Marketing" : "Select Role");

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="w-full h-[36px] px-3 text-[12px] border border-slate-200 rounded-md text-[13px] text-slate-800 bg-white cursor-pointer flex items-center justify-between shadow-sm focus:outline-none focus:border-slate-800 transition-all"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-[100] top-[calc(100%+4px)] left-0 w-full bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto max-h-[210px] animate-in fade-in zoom-in-95 duration-100">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`px-3 py-2 text-[12px] cursor-pointer hover:bg-slate-50 transition-colors ${value === opt.value ? 'bg-slate-50 font-bold text-slate-900' : 'text-slate-700'}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const roleDescriptions = [
  { label: "Admin", color: "#6366f1", desc: "Full access to all modules, settings and team management" },
  { label: "Designer", color: "#ec4899", desc: "Handles design tasks, quotations and client design activities" },
  { label: "Accountant", color: "#f59e0b", desc: "Manages transactions, invoices and financial records" },
  { label: "Store Incharge", color: "#10b981", desc: "Manages store inventory, stock and material requests" },
  { label: "Field Marketing", color: "#3b82f6", desc: "Handles site visits, client coordination and on-field tasks" },
  { label: "Client Coordinator", color: "#8b5cf6", desc: "Coordinates client communications, operations and support tasks" },
  { label: "Social Media", color: "#06b6d4", desc: "Manages social media presence, content and digital marketing" },
];

const RoleInfoPopover = () => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="ml-1.5 w-[15px] h-[15px] rounded-full bg-slate-200 hover:bg-slate-800 text-slate-500 hover:text-white flex items-center justify-center transition-all duration-200 focus:outline-none"
        title="View role descriptions"
      >
        <Info className="w-[9px] h-[9px]" />
      </button>

      {open && (
        <div className="absolute left-6 top-0 z-[200] w-[270px] bg-white border border-slate-200 rounded-xl shadow-2xl p-3 animate-in fade-in slide-in-from-left-2 duration-150">
          <div className="flex items-center gap-1.5 mb-2.5 pb-2 border-b border-slate-100">
            <Info className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Role Descriptions</span>
          </div>
          <div className="flex flex-col gap-2">
            {roleDescriptions.map((r) => (
              <div key={r.label} className="flex items-start gap-2">
                <span
                  className="mt-[3px] w-[7px] h-[7px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: r.color }}
                />
                <div>
                  <span className="text-[11px] font-bold text-slate-800">{r.label}</span>
                  <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AddEmployeeModal = ({ isOpen, onClose, onSubmit, isLoading, initialData }) => {
  const {
    register,
    submit,
    errors,
    role,
    locations,
    isEdit,
    setValue,
    control
  } = useEmployeeForm({ isOpen, initialData, onSubmit });

  const [toast, setToast] = React.useState(null);
  const todayDate = new Date().toISOString().split('T')[0];

  const onInvalid = (formErrors) => {
    const first = Object.values(formErrors || {})?.[0];
    const msg = first?.message || 'Please fix the highlighted fields.';
    setToast({ message: msg, type: 'error' });
  };

  // Shared classes for elegant high-contrast elements inside white modal
  const inputClass = "text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all h-[36px] shadow-sm rounded-md";
  const fieldLabel = (text, req = false) => (
    <span className="text-slate-700 font-bold text-[12px] tracking-wide">
      {text} {req && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex flex-col gap-0.5 z-10 relative">
            <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
              VERVE KITCHENS
            </span>
            <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
              {isEdit ? 'Modify Employee / Contractor Profile' : 'Employee / Contractor Onboarding Panel'}
            </span>
          </div>
        }
        size="lg"
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-center w-full gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:flex-1 cursor-pointer py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-800 rounded-md text-[12px] font-semibold transition-all duration-300 ease-in-out shadow-sm bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="add-employee-form"
              disabled={isLoading}
              className="w-full sm:flex-1 cursor-pointer py-2.5 min-h-[38px] bg-slate-900 hover:bg-black text-white rounded-md text-[12px] font-bold transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed border border-slate-900 shadow-sm"
            >
              {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Member'}
            </button>
          </div>
        }
      >
        {/* Form fields directly laid out on a clean white background */}
        <form id="add-employee-form" onSubmit={submit(onInvalid)} className="flex flex-col gap-4">
          {/* Row 1: First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={fieldLabel('First Name', true)} error={errors.firstName} field="firstName">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    className={inputClass}
                    placeholder="First name"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      const val = e.target.value.toLowerCase().replace(/\s+/g, '');
                      if (val) {
                        setValue('email', `${val}@pinkmoontech.com`, { shouldValidate: true });
                      } else {
                        setValue('email', '');
                      }
                    }}
                  />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Last Name')} error={errors.lastName} field="lastName">
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input id="lastName" className={inputClass} placeholder="Last name" {...field} />
                )}
              />
            </FormField>
          </div>

          {/* Row 2: Phone & Email */}
          <div className={`grid grid-cols-1 ${role !== 'installation_team' ? 'sm:grid-cols-2' : ''} gap-4`}>
            <FormField label={fieldLabel('Phone Number', true)} error={errors.phone} field="phone">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input id="phone" className={inputClass} placeholder="10 digit mobile" {...field} maxLength={10} inputMode="numeric" />
                )}
              />
            </FormField>

            {role !== 'installation_team' && (
              <FormField label={fieldLabel('Email Address', true)} error={errors.email} field="email">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input id="email" className={inputClass} placeholder="email@pinkmoontech.com" type="email" {...field} />
                  )}
                />
              </FormField>
            )}
          </div>

          {/* Row 3: Employee ID & Role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={fieldLabel('Employee ID', true)} error={errors.employeeId} field="employeeId">
              <Controller
                name="employeeId"
                control={control}
                render={({ field }) => (
                  <Input
                    id="employeeId"
                    className={inputClass}
                    placeholder="EMP001"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  />
                )}
              />
            </FormField>

            <FormField
              label={
                <span className="flex items-center">
                  {fieldLabel('Role Assignment', true)}
                  {/* <RoleInfoPopover /> */}
                </span>
              }
              error={errors.role}
              field="role"
            >
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <CustomRoleSelect value={field.value} onChange={field.onChange} />
                )}
              />
            </FormField>
          </div>

          {/* Dynamic Grid Pairing depending on whether Role Type (installation member) is active */}
          {role === 'installation_team' ? (
            <>
              {/* Row 4: Role Type, Joining Date & Work Location */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField label={fieldLabel('Role Type', true)} error={errors.installationTrade}>
                  <Controller
                    name="installationTrade"
                    control={control}
                    render={({ field }) => (
                      <Select className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select type</option>
                        {INSTALLATION_TRADES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Joining Date', true)} error={errors.joiningDate} field="joiningDate">
                  <Controller
                    name="joiningDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="joiningDate" className={`${inputClass} cursor-pointer`} type="date" max={todayDate} {...field} />
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Work Location', true)} error={errors.workLocation} field="workLocation">
                  <Controller
                    name="workLocation"
                    control={control}
                    render={({ field }) => (
                      <Select id="workLocation" className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select location</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc.name}>
                            {loc.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </>
          ) : (
            <>
              {/* Row 4: Joining Date, Work Location & Password */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField label={fieldLabel('Joining Date', true)} error={errors.joiningDate} field="joiningDate">
                  <Controller
                    name="joiningDate"
                    control={control}
                    render={({ field }) => (
                      <Input id="joiningDate" className={`${inputClass} cursor-pointer`} type="date" max={todayDate} {...field} />
                    )}
                  />
                </FormField>

                <FormField label={fieldLabel('Work Location', true)} error={errors.workLocation} field="workLocation">
                  <Controller
                    name="workLocation"
                    control={control}
                    render={({ field }) => (
                      <Select id="workLocation" className={`${inputClass} pr-10`} {...field}>
                        <option value="">Select location</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc.name}>
                            {loc.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>

                <FormField
                  label={fieldLabel(isEdit ? 'New Password' : 'Password', !isEdit)}
                  error={errors.password}
                  field="password"
                >
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="password"
                        className={inputClass}
                        placeholder={
                          isEdit
                            ? 'Leave blank'
                            : '8–10 chars: 1 uppercase'
                        }
                        type="password"
                        maxLength={10}
                        {...field}
                        autoComplete="new-password"
                      />
                    )}
                  />
                </FormField>
              </div>
            </>
          )}

          {/* Notes */}
          <FormField label={fieldLabel('Administrative Notes')} error={errors.notes}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea className="text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all font-medium text-[12px] shadow-sm rounded-md" placeholder="Background info or notes..." {...field} rows={2} />
              )}
            />
          </FormField>
        </form>
      </Modal>
    </>
  );
};
